import React, { useCallback, useEffect, useState } from 'react'
import { CRow, CCol, CCard, CCardBody, CButton, CAlert, CFormSwitch } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPlus, cilPeople, cilLayers, cilSpeedometer } from '@coreui/icons'
import AddForm from './AddForm'
import EditForm from './EditForm'
import Table from './Table'
import TeamService from 'src/service/TeamService'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import StepperBar from '../SetupProgress/StepperBar'
import InfoTooltip from '../GameStructure/InfoTooltip'

const Team = () => {
  const [gradeList, setGradeList] = useState([])
  const [users, setUsers] = useState([])
  const [show, setShow] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [gameType, setGameType] = useState({})
  const [addTeamVisible, setAddTeamVisible] = useState(false)
  const [editTeamId, setEditTeamId] = useState(null)
  const [maxTeams, setMaxTeams] = useState(null)

  useEffect(() => {
    TeamService.getAddTeamData()
      .then((res) => {
        if (res.status === 200) {
          setGradeList(res.grade_list)
          setShow(res.is_team_activate)
        }
      })
      .catch((e) => {
        ToastComponent(e.response?.data?.message, 'error')
      })
  }, [])

  useEffect(() => {
    CommonService.getGameActivateInfo().then((res) => {
      if (res.status === 200) {
        const currentPlan = res.gamePricingPlanDetails || res.pendingPricingPlan
        setMaxTeams(currentPlan?.max_teams ?? null)
      }
    })
  }, [])

  const fetchUsers = useCallback(async () => {
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    try {
      const result = await TeamService.getTeam(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
      setGameType(result.game_type)
    } catch (error) {
      console.error('Error fetching teams:', error)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const handleActivateChange = (e) => {
    setShow(e.target.checked)
    TeamService.activateTeamStatus({ status: e.target.checked }).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
      }
    })
  }

  return (
    <>
      <StepperBar activeStepKey="teams" />
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h4 className="mb-1">Teams</h4>
          <div className="text-medium-emphasis">Manage teams, comps, sponsors and logos.</div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <CFormSwitch
            label="Show Teams"
            id="show_teams"
            checked={!!show}
            onChange={handleActivateChange}
          />
          <CButton color="primary" onClick={() => setAddTeamVisible(true)}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Team
          </CButton>
        </div>
      </div>

      {gameType == 4 && (
        <CAlert color="danger">Restriction: You can create a maximum of 2 Team Names only</CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Total Teams</div>
                <div className="fs-4 fw-semibold">{users?.total ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilPeople} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Comps Available</div>
                <div className="fs-4 fw-semibold">{gradeList?.length ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-info-subtle text-info"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilLayers} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        {gameType != 4 && maxTeams && (
          <CCol sm={6} lg={4}>
            <CCard className="h-100">
              <CCardBody className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-medium-emphasis small">
                    Teams Allowed
                    <InfoTooltip content="Upgrade your plan to create additional teams (when quota has been reached)." />
                  </div>
                  <div className="fs-4 fw-semibold">
                    {users?.total ?? 0}/{maxTeams}
                  </div>
                </div>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning"
                  style={{ width: 42, height: 42 }}
                >
                  <CIcon icon={cilSpeedometer} size="lg" />
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        )}
      </CRow>

      <CCard>
        <CCardBody>
          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnFilter={setColumnFilter}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnFilter={columnFilter}
            columnSorter={columnSorter}
            onEditTeam={setEditTeamId}
          />
        </CCardBody>
      </CCard>

      <AddForm
        visible={addTeamVisible}
        onClose={() => setAddTeamVisible(false)}
        gradeList={gradeList}
        fetchUsers={fetchUsers}
      />

      <EditForm
        teamId={editTeamId}
        visible={!!editTeamId}
        onClose={() => setEditTeamId(null)}
        gradeList={gradeList}
        gameType={gameType}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default Team
